import { useState } from 'react';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';
import { useRouter } from '@polyai/common/hooks/useRouter';
import Button from '@polyai/ui/components/atoms/Button';

import Helmet from 'components/molecules/Helmet';
import { trackPendoEvent, updatePendoVisitorMetadata } from 'lib/pendo';
import { generateNextPath } from 'lib/routeHelper';
import { LeftPanel } from 'screens/AgentWizard/components/LeftPanel';
import ProgressSliderWrapper from 'screens/AgentWizard/components/ProgressSliderWrapper';
import { Wrapper } from 'screens/AgentWizard/components/Wrapper';
import {
  ASSETS,
  PENDO_EVENTS,
  WIZARD_CONTENT_WIDTH,
  WIZARD_STEPS,
} from 'screens/AgentWizard/constants';
import AGENT_WIZARD_PATHS from 'screens/AgentWizard/paths';
import {
  EMPLOYEE_COUNT_OPTIONS,
  REFERRAL_OPTIONS,
  ROLE_OPTIONS,
  USAGE_OPTIONS,
} from './constants';
import { REFERRAL_ICONS } from './referralIcons';
import {
  Content,
  ContentContainer,
  FixedBottomSection,
  Logo,
  MobileLogo,
  OptionButton,
  OptionsGrid,
  Question,
  QuestionLabel,
  RightPanel,
  ScrollableSection,
  Title,
} from './Survey.styled';
import type { EmployeeCount, Referral, Role, Usage } from './types';

const AgentWizardSurvey = () => {
  const { pushUrl } = useRouter();
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();

  const [employeeCount, setEmployeeCount] = useState<EmployeeCount | null>(
    null,
  );
  const [role, setRole] = useState<Role | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [referral, setReferral] = useState<Referral | null>(null);

  const handleSubmit = () => {
    const surveyData = {
      survey_employee_count: employeeCount,
      survey_referral: referral,
      survey_role: role,
      survey_usage: usage,
    };

    // Track as event (for analytics)
    trackPendoEvent(PENDO_EVENTS.SURVEY_COMPLETED, {
      accountId,
      projectId,
      ...surveyData,
    });

    // Store on visitor profile (for segmentation, like poll responses)
    updatePendoVisitorMetadata(surveyData);

    pushUrl(
      generateNextPath(AGENT_WIZARD_PATHS.VOICE, { accountId, projectId }),
    );
  };

  return (
    <>
      <Helmet title="Agent Wizard - Survey" />
      <Wrapper>
        <LeftPanel bgUrl={ASSETS.LOGIN_BG}>
          <Logo aria-label="PolyAI" />
        </LeftPanel>
        <RightPanel>
          <MobileLogo aria-label="PolyAI" />
          <ProgressSliderWrapper step={WIZARD_STEPS.SURVEY} showResetButton />
          <ContentContainer>
            <Content>
              <ScrollableSection>
                <div>
                  <Title>
                    While we&apos;re building your agent tell us about yourself
                  </Title>

                  <Question>
                    <QuestionLabel>
                      1. What&apos;s your number of employees?
                    </QuestionLabel>
                    <OptionsGrid $columns={3}>
                      {EMPLOYEE_COUNT_OPTIONS.map(option => (
                        <OptionButton
                          key={option}
                          $selected={employeeCount === option}
                          type="button"
                          onClick={() => setEmployeeCount(option)}
                        >
                          {option}
                        </OptionButton>
                      ))}
                    </OptionsGrid>
                  </Question>

                  <Question>
                    <QuestionLabel>2. What is your role?</QuestionLabel>
                    <OptionsGrid $columns={2}>
                      {ROLE_OPTIONS.map(option => (
                        <OptionButton
                          key={option}
                          $selected={role === option}
                          type="button"
                          onClick={() => setRole(option)}
                        >
                          {option}
                        </OptionButton>
                      ))}
                    </OptionsGrid>
                  </Question>

                  <Question>
                    <QuestionLabel>
                      3. What are you using PolyAI for?
                    </QuestionLabel>
                    <OptionsGrid $columns={3}>
                      {USAGE_OPTIONS.map(option => (
                        <OptionButton
                          key={option}
                          $selected={usage === option}
                          type="button"
                          onClick={() => setUsage(option)}
                        >
                          {option}
                        </OptionButton>
                      ))}
                    </OptionsGrid>
                  </Question>

                  <Question>
                    <QuestionLabel>
                      4. Where did you hear about us?
                    </QuestionLabel>
                    <OptionsGrid $columns={3}>
                      {REFERRAL_OPTIONS.map(option => (
                        <OptionButton
                          key={option}
                          $selected={referral === option}
                          type="button"
                          onClick={() => setReferral(option)}
                        >
                          {REFERRAL_ICONS[option]}
                          {option}
                        </OptionButton>
                      ))}
                    </OptionsGrid>
                  </Question>
                </div>
              </ScrollableSection>

              <FixedBottomSection>
                <Button
                  data-test-id="agent-wizard-survey-submit-button"
                  label="Continue"
                  style={{
                    maxWidth: `${WIZARD_CONTENT_WIDTH}px`,
                    margin: '0 auto',
                  }}
                  fullWidth
                  onClick={handleSubmit}
                />
              </FixedBottomSection>
            </Content>
          </ContentContainer>
        </RightPanel>
      </Wrapper>
    </>
  );
};

export default AgentWizardSurvey;
